import React from 'react';
import { printHtml, URDU_PRINT_FONT } from '../../utils/printDocument';
import { savePdf } from '../../utils/pdfDocument';
import { formatClinicDate, formatClinicTime } from '../../utils/clinicTime';
import { PAYMENT_MODE_LABEL } from '../../constants/payments';
import { toPaisa, formatPaisa, formatSigned } from '../../utils/money';

/**
 * The 80 mm receipt a patient takes home from the billing counter, made the way the token slip is
 * (TokenPrintModal): the same markup is the preview in the receipt dialog and, copied into a small
 * window under the thermal stylesheet below, the printed slip. The Tailwind classes style the
 * preview; the rcpt- classes style the paper, where the app's stylesheet is not loaded.
 *
 * Lines print at their full price with the discount under them, so Items less the Discount plus the
 * Round off is exactly the Total the patient paid.
 */

// The thermal-printer stylesheet, on the token slip's measurements: 80 mm, Courier, dashed rules.
const RECEIPT_STYLES = `
  @page { size: 80mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; padding: 5mm; background: white; color: #000; }
  .rcpt-header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
  .rcpt-name { font-size: 16px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
  .rcpt-contact { font-size: 11px; }
  .rcpt-section { border-bottom: 1px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
  .rcpt-row { display: flex; justify-content: space-between; gap: 8px; margin: 3px 0; }
  .rcpt-label { font-weight: bold; }
  .rcpt-value { text-align: right; }
  .rcpt-item { margin: 5px 0; }
  .rcpt-item-name { word-break: break-word; }
  /* The working sits under the name, indented, so the eye reads name then sum then amount. */
  .rcpt-item-calc { padding-left: 8px; }
  .rcpt-notice { margin-top: 2px; text-align: center; font-size: 11px; }
  .rcpt-total { font-size: 14px; font-weight: bold; }
  .rcpt-subsidy { text-align: center; margin-top: 8px; font-size: 13px; }
  .rcpt-amount { font-family: 'Courier New', monospace; font-weight: bold; font-size: 16px; unicode-bidi: isolate; margin: 0 5px; }
  .rcpt-footer { text-align: center; margin-top: 10px; font-size: 11px; }
  .rcpt-footer > div { margin-top: 4px; }
  /* Urdu in the face and weight the token slip prints it in, so the two slips a patient holds match.
     Nastaliq draws roughly 2.5x its font size between the top of the tallest letter and the tail of
     the lowest, so a line box of 1.625 let the hospital name paint over the address under it. */
  .urdu-text { font-family: ${URDU_PRINT_FONT}; font-weight: 600; line-height: 2.5; direction: rtl; text-align: center; }
`;

// A name that already starts with a title keeps it. "Dr." goes only on a bare name, so a doctor saved
// as "Mr. Adeel Riaz" does not print as "Dr. Mr. Adeel Riaz".
const TITLED = /^(dr|prof|mr|mrs|ms|miss)\b\.?/i;

/** "Dr. Sana Iqbal", from the prescription's doctor, titled once. Empty when unknown. */
export const receiptDoctorName = (invoice) => {
  const d = invoice?.prescription?.doctor;
  if (!d) return '';
  const base = (d.fullName || `${d.firstName || ''} ${d.lastName || ''}`).trim();
  if (!base) return '';
  return TITLED.test(base) ? base : `Dr. ${base}`;
};

/**
 * A bill's working, in paisa: every line at its full price, everything taken off (line discounts
 * and the bill's additional discount together), tax, the round off, and what was charged. Gross
 * less discount plus tax plus round off is the total. The slip, the A4 invoice and the invoice
 * page all show the bill this way.
 */
export const invoiceTotals = (invoice) => {
  const items = invoice?.items || [];
  return {
    gross: items.reduce((s, i) => s + toPaisa(i.unitPrice) * (Number(i.quantity) || 0), 0),
    discount: items.reduce((s, i) => s + toPaisa(i.discountAmount), 0) + toPaisa(invoice?.discountAmount),
    tax: toPaisa(invoice?.taxAmount),
    rounding: toPaisa(invoice?.roundingAdjustment),
    total: toPaisa(invoice?.grandTotal),
  };
};

/**
 * Every Urdu line on the slip carries its alignment and leading inline rather than by class.
 * App.css sets a global `.urdu-text { text-align: right }`, which outranks the `text-center` on the
 * blocks here and pushed the hospital name and the closing line off to the right; and Nastaliq's
 * ink is about 2.5x the font size tall, so the default line box let the name overlap the address
 * below it. printInvoiceReceipt copies this markup into the print window, so the same inline rules
 * land on the paper as well as in the preview.
 */
const URDU_LINE = { textAlign: 'center', lineHeight: 2.5 };

const formatDate = (value) => formatClinicDate(value, { day: '2-digit', month: 'short', year: 'numeric' }, '');
const formatTime = (value) => formatClinicTime(value, { hour: '2-digit', minute: '2-digit', hour12: true }, '');

const Row = ({ label, children, className = '' }) => (
  <div className={`rcpt-row flex justify-between gap-3 mb-1 ${className}`}>
    <span className="rcpt-label font-semibold">{label}</span>
    <span className="rcpt-value text-right">{children}</span>
  </div>
);

const Section = ({ children }) => (
  <div className="rcpt-section border-b border-dashed border-gray-400 pb-3 mb-3 text-sm">{children}</div>
);

/** The slip. Rendered in the receipt dialog; its markup is what printInvoiceReceipt prints. */
export const InvoiceReceiptSlip = ({ invoice, branding }) => {
  if (!invoice) return null;
  const profile = branding?.profile || {};
  const name = profile.nameEnglish || 'Perveen Aslam Memorial Hospital';
  const nameUrdu = profile.nameUrdu || 'پروین اسلم میموریل ہسپتال';
  const items = [...(invoice.items || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  const { gross, discount, tax, total } = invoiceTotals(invoice);
  const paidAt = invoice.paidAt || invoice.issuedAt || invoice.createdAt;
  const patient = invoice.patient || {};
  const doctor = receiptDoctorName(invoice);
  const isCash = invoice.paymentMode === 'CASH' && invoice.cashTendered != null;

  return (
    <div className="rcpt text-gray-900">
      <div className="rcpt-header text-center border-b border-dashed border-gray-400 pb-3 mb-3">
        <div className="rcpt-name text-lg font-bold tracking-wide uppercase leading-snug">{name}</div>
        <div className="urdu-text text-sm text-gray-700" dir="rtl" style={URDU_LINE}>{nameUrdu}</div>
        {profile.address && <div className="rcpt-contact text-xs text-gray-600">{profile.address}</div>}
        {profile.phone && <div className="rcpt-contact text-xs text-gray-600">{`Tel: ${profile.phone}`}</div>}
      </div>

      <Section>
        <Row label="Invoice#:">{invoice.invoiceNumber || ''}</Row>
        {paidAt && <Row label="Date:">{`${formatDate(paidAt)} ${formatTime(paidAt)}`.trim()}</Row>}
        <Row label="Patient Name:">{`${patient.firstName || ''} ${patient.lastName || ''}`.trim()}</Row>
        {patient.mrn && <Row label="MR#:">{patient.mrn}</Row>}
        {invoice.prescription?.prescriptionNumber && <Row label="Rx#:">{invoice.prescription.prescriptionNumber}</Row>}
        {doctor && <Row label="Doctor:">{doctor}</Row>}
      </Section>

      <Section>
        {/* Name on its own line, because a medicine name is long and the slip is 80 mm; under it
            the working the owner asked for (2026-10-05): how many, what one costs, and what the
            line came to. Discounts stay summarised in the block below rather than per line, so
            Items less Discount plus Round off is still exactly the Total. */}
        {items.map((i, idx) => {
          const qty = Number(i.quantity) || 0;
          const unit = toPaisa(i.unitPrice);
          return (
            <div key={i.id || i.prescriptionMedicine?.id || idx} className="rcpt-item mb-1.5">
              <div className="rcpt-item-name">{i.name}</div>
              <div className="rcpt-row rcpt-item-calc flex justify-between gap-3">
                <span>{`${qty} x ${formatPaisa(unit)}`}</span>
                <span className="rcpt-value text-right font-semibold">{formatPaisa(unit * qty)}</span>
              </div>
            </div>
          );
        })}
      </Section>

      <Section>
        <Row label="Items:">{formatPaisa(gross)}</Row>
        {discount > 0 && <Row label="Discount:" className="text-emerald-600">{`-${formatPaisa(discount)}`}</Row>}
        {tax > 0 && <Row label="Tax:">{formatPaisa(tax)}</Row>}
        {toPaisa(invoice.roundingAdjustment) !== 0 && <Row label="Round off:">{formatSigned(invoice.roundingAdjustment)}</Row>}
        <Row label="Total:" className="rcpt-total text-base font-bold">{`Rs ${formatPaisa(total)}`}</Row>
      </Section>

      {/* The payment, and under it what the hospital took off. A fully discounted bill used to print
          "No charge" here; the owner took it off, so on such a bill this block is the Urdu sentence
          alone, and the section is skipped entirely when there is neither a payment nor a discount. */}
      {(total > 0 || discount > 0) && (
        <Section>
          {total > 0 && (
            <>
              <Row label="Paid by:">{PAYMENT_MODE_LABEL[invoice.paymentMode] || invoice.paymentMode || ''}</Row>
              {invoice.paymentReference && <Row label="Reference:">{invoice.paymentReference}</Row>}
              {isCash && (
                <>
                  <Row label="Received:">{`Rs ${formatPaisa(toPaisa(invoice.cashTendered))}`}</Row>
                  <Row label="Change:">{`Rs ${formatPaisa(toPaisa(invoice.changeReturned))}`}</Row>
                </>
              )}
            </>
          )}
          {/* What the hospital took off this bill, said in Urdu because it is the patient who reads
              it, in the same words the A4 invoice uses. Western digits per the house rule, and the
              amount is bidi-isolated (U+2066..U+2069) so "Rs 231.93" cannot be reordered inside the
              right-to-left sentence. Bold and underlined: it is the figure the patient looks for.
              Only when something was actually taken off. */}
          {discount > 0 && (
            <div className="rcpt-subsidy urdu-text text-sm text-center mt-2" dir="rtl" style={URDU_LINE}>
              ہسپتال کی جانب سے آپ کو
              <span
                className="rcpt-amount font-bold mx-1.5"
                style={{ direction: 'ltr', unicodeBidi: 'isolate', fontFamily: "'Courier New', monospace", fontWeight: 700, fontSize: '16px' }}
              >
                {`⁦Rs ${formatPaisa(discount)}⁩`}
              </span>
              کی رعایت دی گئی ہے
            </div>
          )}
        </Section>
      )}

      {/* The return policy, in both languages, because it is the patient who has to read it and
          the counter is where it gets argued (owner, 2026-10-05). It sits under the last dashed
          rule and above the thanks, so it reads as a notice rather than as part of the figures;
          it carries no rule of its own, or the slip prints two in a row. */}
      <div className="rcpt-notice text-center">
        <div className="text-xs text-gray-700">Opened or loose tablets cannot be returned or exchanged.</div>
        <div className="urdu-text text-sm text-gray-700" dir="rtl" style={URDU_LINE}>
          کھلی ہوئی یا کٹی ہوئی گولیاں واپس یا تبدیل نہیں ہوں گی
        </div>
      </div>

      <div className="rcpt-footer text-center mt-4">
        <div className="text-xs text-gray-600">Thank you</div>
        {/* The patient is asked to keep the hospital in their prayers, in place of the plain
            "شکریہ" that used to sit here. The owner's wording. */}
        <div className="urdu-text text-sm text-gray-700" dir="rtl" style={URDU_LINE}>ہمیں اپنی دعاؤں میں یاد رکھیں</div>
      </div>
    </div>
  );
};

/**
 * Prints the slip rendered in `node` (the receipt dialog's preview) in a small window of its own, the
 * way printTokenReceipt prints a token. Returns false when there is nothing to print or the browser
 * blocked the popup, so the caller can say so.
 */
export const printInvoiceReceipt = (node, { title = 'Invoice Receipt' } = {}) => {
  if (!node) return false;
  return printHtml({
    title,
    styles: RECEIPT_STYLES,
    bodyHtml: node.innerHTML,
    features: 'width=300,height=600',
  });
};

/** The slip rendered in `node`, saved as an 80 mm PDF the length of the slip. */
export const saveInvoiceReceiptPdf = (node, { title = 'Invoice Receipt', fileName } = {}) =>
  savePdf({ title, fileName: fileName || title, styles: RECEIPT_STYLES, bodyHtml: node.innerHTML, paper: 'SLIP' });
